import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, FormControlLabel, FormHelperText, Switch, Typography } from '@mui/material';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/stores/notificationStore';
import { formatDateTime } from '@/utils/locale';
import { getPersonalPermissions, setPersonalPermission } from '@/api/releaseApproval';

interface PersonalPermissionsSectionProps {
  userId: string;
}

/**
 * Personal permissions of one team member (#3316). Only `release.approve`
 * exists: approving a test version for production is granted to a person,
 * never to a role. Admins only; the change applies at once and is recorded
 * with the granting admin.
 */
export function PersonalPermissionsSection({ userId }: PersonalPermissionsSectionProps) {
  const { t } = useTranslation('releaseApproval');
  const notify = useNotify();
  const queryClient = useQueryClient();
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const queryKey = ['personal-permissions', userId];

  const permissions = useQuery({
    queryKey,
    queryFn: () => getPersonalPermissions(userId),
    enabled: isAdmin,
  });

  const toggle = useMutation({
    mutationFn: (granted: boolean) => setPersonalPermission(userId, 'release.approve', granted),
    onSuccess: (_result, granted) => {
      notify.success(t(granted ? 'personal.granted' : 'personal.revoked'));
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      notify.error(t('personal.saveFailed'));
    },
  });

  if (!isAdmin || !permissions.data) return null;

  const grant = permissions.data.data.find((g) => g.permission === 'release.approve');
  const { grantable } = permissions.data;

  return (
    <Box data-testid="personal-permissions">
      <Typography variant="subtitle2" gutterBottom>
        {t('personal.title')}
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(grant)}
            disabled={!grantable || toggle.isPending}
            onChange={(event) => {
              toggle.mutate(event.target.checked);
            }}
          />
        }
        label={t('personal.releaseApprove')}
      />
      <FormHelperText>
        {grant
          ? t('personal.grantedBy', {
              name: grant.grantedByName ?? t('personal.unknownAdmin'),
              time: formatDateTime(grant.grantedAt),
            })
          : t('personal.releaseApproveHelp')}
      </FormHelperText>
      {!grantable && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('personal.testEnvironment')}
        </Alert>
      )}
    </Box>
  );
}
